import React, { useState, useEffect } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    Flex,
    useDisclosure,
    useToast,
    Spinner,
    Center,
} from '@chakra-ui/react';
import { SettingsIcon } from '@chakra-ui/icons';
import ClientConfigApi from 'api/clientconfig';
import InputField from 'components/formComponents/inputField';
import { Formik, Form } from 'formik';

export default function ConfigModal(props) {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const initialRef = React.useRef(null);
    const finalRef = React.useRef(null);
    const [loading, setLoading] = useState(false);
    const [configData, setConfigData] = useState(null);

    const fetchConfig = async () => {
        try {
            setLoading(true);
            const response = await ClientConfigApi.Get();
            if (response.data.status_code === 200 && response?.data?.data?.length > 0) {
                setConfigData(response.data.data[0]);
            } else {
                toast({
                    title: 'Failed to load configuration',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                    position: 'top',
                });
            }
        } catch (err) {
            console.error(err);
            toast({
                title: 'Error loading configuration',
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleOpen = () => {
        onOpen();
        fetchConfig();
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const payload = {
                ...configData,
                gold_buy_sell_price_gap: values.gold_buy_sell_price_gap,
            };

            const response = await ClientConfigApi.UpdateBullionGap(payload);
            if (response.data.status_code === 200) {
                toast({
                    title: 'Configuration Updated Successfully',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                    position: 'top',
                });
                setConfigData(payload);
                onClose();
                if (props.func) props.func();
            } else {
                toast({
                    title: response?.data?.message || 'Error occurred while updating',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                    position: 'top',
                });
            }
        } catch (err) {
            console.error(err);
            toast({
                title: 'Error updating configuration',
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Button
                variant='lightBrand'
                fontSize='sm'
                fontWeight='500'
                borderRadius='10px'
                boxShadow='lg'
                px='15px'
                py='5px'
                disabled={props.disabled}
                onClick={handleOpen}>
                <SettingsIcon mr={2} /> Config
            </Button>
            <Modal
                initialFocusRef={initialRef}
                finalFocusRef={finalRef}
                isOpen={isOpen}
                onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{props.head || 'Bullion Settings'}</ModalHeader>
                    <ModalCloseButton />

                    <ModalBody pb={6}>
                        {loading || !configData ? (
                            <Center py={10}>
                                <Spinner size='xl' />
                            </Center>
                        ) : (
                            <Formik
                                initialValues={{ gold_buy_sell_price_gap: configData.gold_buy_sell_price_gap || 0 }}
                                onSubmit={handleSubmit}>
                                {({ isSubmitting, setFieldValue }) => (
                                    <Form>
                                        <InputField
                                            label='Gold Buy/Sell Price Gap (%)'
                                            name='gold_buy_sell_price_gap'
                                            type='number'
                                            placeholder='Gap Percentage'
                                            required
                                            onKeyDown={(e) => {
                                                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
                                            }}
                                            onWheel={(e) => e.target.blur()}
                                        />
                                        <ModalFooter px={0} mt={4}>
                                            <Button
                                                colorScheme='blue'
                                                mr={3}
                                                type='submit'
                                                isLoading={isSubmitting}>
                                                Save
                                            </Button>
                                            <Button onClick={onClose}>Cancel</Button>
                                        </ModalFooter>
                                    </Form>
                                )}
                            </Formik>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
}
